import { ComponentProps, forwardRef, ReactNode } from "react";

import { Box } from "./box";
import { Label } from "./label";
import { Typography } from "./typography";
import { cn } from "./cn";

type InputBaseProps = Omit<ComponentProps<"input">, "name"> & {
  label?: string;
  errorMessage?: string;
  buttonSection?: () => ReactNode;
};

// cell = 테두리 없는 표 칸(hover 에만 테두리) · outline = 표 밖 단독 숫자칸·폰 시트 칸(늘 보이는 테두리) — 둘 다 외곽 Box·label·오류 글자 없이 input 하나만 그리고 py-1 text-sm 고정이라 inputSize 를 받지 않는다.
export type InputProps = InputBaseProps &
  (
    | {
        variant?: "default";
        name: string;
        // 높이·글씨 — Button·Select 의 size 와 맞춘다(default 40px · sm 36px) · xs 32px 는 표 칸 안 편집 전용 · lg 44px 는 고객 화면 터치용(16px 글씨 — iOS 확대 방지).
        inputSize?: "default" | "sm" | "xs" | "lg";
        // 칸 위에 겹쳐 그린 아이콘(start — 검색 돋보기)·표식(end — 수량식 「식」) 자리를 글자와 안 겹치게 비운다.
        adornment?: "start" | "end";
      }
    | { variant: "cell" | "outline"; name?: string; inputSize?: never; adornment?: never }
  );

const INPUT_SIZE_CLASS = {
  default: "min-h-10 py-2 text-md",
  sm: "min-h-9 py-1 text-sm",
  xs: "min-h-8 px-2 py-1 text-sm",
  lg: "min-h-11 py-2 text-lg",
} as const;

// phone:text-lg — 폰에서는 크기와 상관없이 16px(16px 미만 입력칸은 iOS Safari 가 포커스 때 화면을 확대한다) · md 이상은 크기별 글씨 그대로.
const CELL_CLASS =
  "w-full rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm phone:text-lg hover:border-border focus:border-ring focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:text-muted-foreground";

// 표 칸과 같은 높이·포커스에 기본 칸의 테두리·배경·좌우 여백(border-input bg-background px-3)을 늘 보인다.
const OUTLINE_CLASS =
  "w-full rounded-md border border-input bg-background px-3 py-1 text-sm phone:text-lg focus:border-ring focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:text-muted-foreground";

const ADORNMENT_CLASS = { start: "pl-8", end: "pr-7" } as const;

// 이메일·비밀번호 칸은 폰 키보드의 첫 글자 대문자·자동 고침·맞춤법 밑줄을 끈다(이메일은 @ 키보드) — 사용처가 넘긴 값이 이긴다.
const credentialProps = (type: string | undefined): ComponentProps<"input"> | undefined =>
  type === "email"
    ? { autoCapitalize: "none", autoCorrect: "off", spellCheck: false, inputMode: "email" }
    : type === "password"
      ? { autoCapitalize: "none", autoCorrect: "off", spellCheck: false }
      : undefined;

const CELL_ERROR_CLASS = "border-destructive focus:border-destructive focus:ring-destructive";

// 사용처가 aria-invalid 만 넘겨도 빨간 테두리(오류 글자 없이 칸만 표시할 때).
const INVALID_CLASS = "aria-[invalid=true]:border-destructive";

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      name,
      errorMessage,
      buttonSection,
      inputSize = "default",
      variant = "default",
      adornment,
      ...props
    },
    ref
  ) => {
    if (variant === "cell" || variant === "outline") {
      return (
        <input
          ref={ref}
          aria-invalid={errorMessage ? true : undefined}
          className={cn(variant === "cell" ? CELL_CLASS : OUTLINE_CLASS, errorMessage && CELL_ERROR_CLASS, className)}
          name={name}
          type={type}
          {...credentialProps(type)}
          {...props}
        />
      );
    }

    return (
      <Box className="flex size-full flex-col items-start gap-1.5">
        {label && <Label htmlFor={name}>{label}</Label>}
        <Box className="flex w-full flex-wrap items-center gap-4">
          <input
            ref={ref}
            className={cn(
              "flex w-full min-w-16 flex-1 self-stretch rounded-md border border-input bg-background px-3 ring-offset-background phone:text-lg file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-placeholder focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              INPUT_SIZE_CLASS[inputSize],
              adornment && ADORNMENT_CLASS[adornment],
              INVALID_CLASS,
              errorMessage &&
                "border-destructive focus-visible:ring-destructive",
              className
            )}
            name={name}
            type={type}
            {...credentialProps(type)}
            {...props}
          />
          {buttonSection && buttonSection()}
        </Box>

        {errorMessage && (
          <Typography className="text-start text-destructive" variant="p">
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }
);
Input.displayName = "Input";

export { Input };
